import { useState, useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Card } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { Spinner } from '@/components/ui/Spinner'
import {
  useRendezVous,
  useAgendaStats,
  useRendezVousEnAttente,
  useConfirmerRendezVous,
  useAnnulerRendezVous,
  useCreateRendezVous,
  useUpdateRendezVous,
  useDeleteRendezVous,
} from '@/hooks/useAgenda'
import { useAuth } from '@/contexts/AuthContext'
import {
  Calendar as CalendarIcon,
  Clock,
  CheckCircle,
  AlertCircle,
  Plus,
  ListTodo,
  Settings2,
} from 'lucide-react'
import toast from 'react-hot-toast'
import type { RendezVous } from '@/services/agenda'
import { agendaService, mapRendezVousToCalendar, mapTaskToCalendar } from '@/services/agenda'
import { emailService } from '@/services/email'
import type { CalendarItem, CalendarViewType } from '@/types/agenda'

import { CalendarView } from '@/components/admin/agenda/CalendarView'
import { PendingAppointments } from '@/components/admin/agenda/PendingAppointments'
import { PastAppointments } from '@/components/admin/agenda/PastAppointments'
import { CreateAppointmentModal } from '@/components/admin/agenda/CreateAppointmentModal'
import { AppointmentDetailModal } from '@/components/admin/agenda/AppointmentDetailModal'
import { TimeSlotManagement } from '@/components/admin/agenda/TimeSlotManagement'

type AgendaTab = 'calendar' | 'slots'

export default function AgendaPage() {
  const { user } = useAuth()
  const professionnelId = user?.id

  // Data fetching
  const { data: rdvsData, isLoading } = useRendezVous({ professionnelId })
  const { data: stats } = useAgendaStats(professionnelId)
  const { data: rdvsEnAttente = [] } = useRendezVousEnAttente(professionnelId)

  // Mutations
  const confirmerRdv = useConfirmerRendezVous()
  const annulerRdv = useAnnulerRendezVous()
  const createRdv = useCreateRendezVous()
  const updateRdv = useUpdateRendezVous()
  const deleteRdv = useDeleteRendezVous()

  // UI state
  const [activeTab, setActiveTab] = useState<AgendaTab>('calendar')
  const [calendarView, setCalendarView] = useState<CalendarViewType>('week')
  const [currentDate, setCurrentDate] = useState(new Date())
  const [showTasks, setShowTasks] = useState(true)
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false)
  const [prefillDate, setPrefillDate] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [selectedRdv, setSelectedRdv] = useState<RendezVous | null>(null)

  // Load calendar tasks
  const { data: calendarTasks = [] } = useQuery({
    queryKey: ['calendar-tasks', professionnelId],
    queryFn: () => agendaService.getTasksForCalendar({ assignedTo: professionnelId }),
    enabled: !!professionnelId && showTasks,
  })

  // Map RDV + tasks to CalendarItem[]
  const calendarItems: CalendarItem[] = useMemo(() => {
    const items: CalendarItem[] = []

    // Map RDV (exclure les annulés du calendrier)
    if (rdvsData?.rendezVous) {
      for (const rdv of rdvsData.rendezVous) {
        if (rdv.statut === 'annule') continue
        items.push(mapRendezVousToCalendar(rdv))
      }
    }

    // Map tasks if toggle is on
    if (showTasks && calendarTasks.length > 0) {
      for (const task of calendarTasks) {
        items.push(mapTaskToCalendar(task))
      }
    }

    return items
  }, [rdvsData, calendarTasks, showTasks])

  // Handlers
  const handleConfirmer = async (rdv: RendezVous) => {
    try {
      const confirmed = await confirmerRdv.mutateAsync(rdv.id)

      const clientEmail = confirmed.client?.email || rdv.client?.email
      const clientName = confirmed.client?.full_name || rdv.client?.full_name || undefined

      if (clientEmail) {
        try {
          const { subject, html } = agendaService.buildAppointmentAcceptedEmail(confirmed)
          await emailService.sendEmail({
            to: clientEmail,
            toName: clientName,
            subject,
            html,
            referenceType: 'rendez_vous',
            referenceId: confirmed.id,
          })
          toast.success('Rendez-vous confirmé et email envoyé au client')
        } catch (err) {
          console.error('Erreur envoi email confirmation RDV:', err)
          toast.success('Rendez-vous confirmé (erreur envoi email)')
        }
      } else {
        toast.success('Rendez-vous confirmé (email client manquant)')
      }
    } catch {
      toast.error('Erreur lors de la confirmation')
    }
  }

  const sendCancellationEmail = async (rdv: RendezVous) => {
    const clientEmail = rdv.client?.email
    if (!clientEmail) return { sent: false, reason: 'missing' as const }
    try {
      const { subject, html } = agendaService.buildAppointmentCancelledEmail(rdv)
      await emailService.sendEmail({
        to: clientEmail,
        toName: rdv.client?.full_name || undefined,
        subject,
        html,
        referenceType: 'rendez_vous',
        referenceId: rdv.id,
      })
      return { sent: true as const }
    } catch (err) {
      console.error('Erreur envoi email annulation RDV:', err)
      return { sent: false, reason: 'error' as const }
    }
  }

  const handleAnnuler = async (rdv: RendezVous) => {
    try {
      const cancelled = await annulerRdv.mutateAsync(rdv.id)
      const ctx: RendezVous = { ...cancelled, client: cancelled.client || rdv.client }
      const result = await sendCancellationEmail(ctx)
      if (result.sent) toast.success('Rendez-vous annulé et email envoyé au client')
      else if (result.reason === 'missing') toast.success('Rendez-vous annulé (email client manquant)')
      else toast.success('Rendez-vous annulé (erreur envoi email)')
    } catch {
      toast.error("Erreur lors de l'annulation")
    }
  }

  const handleSlotClick = (date: Date, hour: number) => {
    const d = new Date(date)
    d.setHours(hour, 0, 0, 0)
    // Format for datetime-local input: YYYY-MM-DDTHH:MM
    const pad = (n: number) => n.toString().padStart(2, '0')
    const formatted = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
    setPrefillDate(formatted)
    setIsCreateModalOpen(true)
  }

  const handleItemClick = async (item: CalendarItem) => {
    if (item.type === 'appointment') {
      try {
        const fullRdv = await agendaService.getRendezVousById(item.rdv_id)
        if (fullRdv) {
          setSelectedRdv(fullRdv)
        }
      } catch {
        toast.error('Erreur lors du chargement du rendez-vous')
      }
    }
  }

  const handleUpdateRdv = async (id: string, updates: {
    client_id?: string
    date_heure?: string
    description?: string | null
    participants?: string[]
  }) => {
    try {
      const result = await updateRdv.mutateAsync({ id, updates })
      // Re-fetch to get full data with client join
      const refreshed = await agendaService.getRendezVousById(result.id)
      if (refreshed) setSelectedRdv(refreshed)
      toast.success('Rendez-vous mis à jour')
    } catch {
      toast.error('Erreur lors de la mise à jour')
    }
  }

  const handleDeleteRdv = async (id: string) => {
    try {
      await deleteRdv.mutateAsync(id)
      setSelectedRdv(null)
      toast.success('Rendez-vous supprimé')
    } catch {
      toast.error('Erreur lors de la suppression')
    }
  }

  const handleAnnulerRdvDetail = async (id: string) => {
    try {
      const cancelled = await annulerRdv.mutateAsync(id)
      const ctx: RendezVous = { ...cancelled, client: cancelled.client || selectedRdv?.client }
      setSelectedRdv(null)
      const result = await sendCancellationEmail(ctx)
      if (result.sent) toast.success('Rendez-vous annulé et email envoyé au client')
      else if (result.reason === 'missing') toast.success('Rendez-vous annulé (email client manquant)')
      else toast.success('Rendez-vous annulé (erreur envoi email)')
    } catch {
      toast.error("Erreur lors de l'annulation")
    }
  }

  const handleCreateRdv = async (data: {
    clientId?: string
    clientEmail: string
    clientName: string
    dateHeure: string
    description: string
    sendConfirmationEmail: boolean
  }) => {
    if (!user?.id) {
      toast.error('Utilisateur non connecté')
      return
    }

    setIsSubmitting(true)
    try {
      // Get or create client
      let clientId = data.clientId
      if (!clientId) {
        const client = await agendaService.getOrCreateClient(data.clientEmail, data.clientName)
        clientId = client.id
      }

      // Create the appointment
      const rdv = await createRdv.mutateAsync({
        client_id: clientId,
        professionnel_id: user.id,
        date_heure: new Date(data.dateHeure).toISOString(),
        description: data.description || null,
        statut: 'en_attente',
      })

      // Send confirmation email if checkbox was checked
      if (data.sendConfirmationEmail && data.clientEmail) {
        try {
          // Re-fetch RDV to get the confirmation_token
          const fullRdv = await agendaService.getRendezVousById(rdv.id)
          if (fullRdv?.confirmation_token) {
            const appUrl = import.meta.env.VITE_APP_URL || window.location.origin
            const { subject, html } = agendaService.buildAppointmentConfirmationEmail(fullRdv, appUrl)
            await emailService.sendEmail({
              to: data.clientEmail,
              toName: data.clientName,
              subject,
              html,
              referenceType: 'rendez_vous',
              referenceId: rdv.id,
            })
            toast.success('Rendez-vous créé et email envoyé')
          } else {
            toast.success('Rendez-vous créé (token de confirmation non disponible)')
          }
        } catch {
          toast.success('Rendez-vous créé (erreur envoi email)')
        }
      } else {
        toast.success('Rendez-vous créé')
      }

      setIsCreateModalOpen(false)
      setPrefillDate('')
    } catch (error) {
      console.error('Erreur création RDV:', error)
      toast.error('Erreur lors de la création du rendez-vous')
    } finally {
      setIsSubmitting(false)
    }
  }

  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-[400px]">
        <Spinner size="lg" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-display font-bold text-brown-900">
            Agenda
          </h1>
          <p className="mt-1 text-brown-600">
            Gérez vos rendez-vous et créneaux de disponibilité
          </p>
        </div>
        <div className="flex items-center gap-3">
          {activeTab === 'calendar' && (
            <>
              <Button
                variant={showTasks ? 'primary' : 'outline'}
                size="sm"
                onClick={() => setShowTasks(!showTasks)}
                leftIcon={<ListTodo className="w-4 h-4" />}
              >
                {showTasks ? 'Masquer' : 'Afficher'} les tâches
              </Button>
              <Button
                onClick={() => {
                  setPrefillDate('')
                  setIsCreateModalOpen(true)
                }}
                leftIcon={<Plus className="w-4 h-4" />}
              >
                Nouveau rendez-vous
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-beige-200">
        <button
          onClick={() => setActiveTab('calendar')}
          className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'calendar'
              ? 'border-mint-600 text-mint-700'
              : 'border-transparent text-brown-500 hover:text-brown-700'
          }`}
        >
          <span className="flex items-center gap-2">
            <CalendarIcon className="w-4 h-4" />
            Calendrier
          </span>
        </button>
        <button
          onClick={() => setActiveTab('slots')}
          className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'slots'
              ? 'border-mint-600 text-mint-700'
              : 'border-transparent text-brown-500 hover:text-brown-700'
          }`}
        >
          <span className="flex items-center gap-2">
            <Settings2 className="w-4 h-4" />
            Créneaux
          </span>
        </button>
      </div>

      {activeTab === 'calendar' ? (
        <>
          {/* Stats */}
          {stats && (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
              <Card padding="sm">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-brown-600">Total</p>
                    <p className="text-2xl font-bold text-brown-900">{stats.total}</p>
                  </div>
                  <CalendarIcon className="w-8 h-8 text-brown-400" />
                </div>
              </Card>
              <Card padding="sm">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-brown-600">En attente</p>
                    <p className="text-2xl font-bold text-gold-600">{stats.en_attente}</p>
                  </div>
                  <AlertCircle className="w-8 h-8 text-gold-500" />
                </div>
              </Card>
              <Card padding="sm">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-brown-600">Confirmés</p>
                    <p className="text-2xl font-bold text-mint-600">{stats.confirme}</p>
                  </div>
                  <CheckCircle className="w-8 h-8 text-mint-500" />
                </div>
              </Card>
              <Card padding="sm">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-brown-600">Aujourd'hui</p>
                    <p className="text-2xl font-bold text-mint-600">{stats.today}</p>
                  </div>
                  <Clock className="w-8 h-8 text-mint-500" />
                </div>
              </Card>
              <Card padding="sm">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-brown-600">Cette semaine</p>
                    <p className="text-2xl font-bold text-brown-600">{stats.thisWeek}</p>
                  </div>
                  <CalendarIcon className="w-8 h-8 text-brown-400" />
                </div>
              </Card>
            </div>
          )}

          {/* Pending appointments */}
          <PendingAppointments
            rdvsEnAttente={rdvsEnAttente}
            onConfirm={handleConfirmer}
            onDecline={handleAnnuler}
            isConfirming={confirmerRdv.isPending}
            isDeclining={annulerRdv.isPending}
          />

          {/* Calendar */}
          <CalendarView
            items={calendarItems}
            view={calendarView}
            onViewChange={setCalendarView}
            onDateChange={setCurrentDate}
            currentDate={currentDate}
            onSlotClick={handleSlotClick}
            onItemClick={handleItemClick}
            isLoading={isLoading}
          />

          {/* Past appointments */}
          <PastAppointments />

          {/* Create appointment modal */}
          <CreateAppointmentModal
            isOpen={isCreateModalOpen}
            onClose={() => {
              setIsCreateModalOpen(false)
              setPrefillDate('')
            }}
            onSubmit={handleCreateRdv}
            isSubmitting={isSubmitting}
            prefillDate={prefillDate}
          />

          {/* Detail/edit appointment modal */}
          <AppointmentDetailModal
            isOpen={!!selectedRdv}
            onClose={() => setSelectedRdv(null)}
            rdv={selectedRdv}
            onUpdate={handleUpdateRdv}
            onCancel={handleAnnulerRdvDetail}
            onDelete={handleDeleteRdv}
            isUpdating={updateRdv.isPending}
            isCancelling={annulerRdv.isPending}
            isDeleting={deleteRdv.isPending}
          />
        </>
      ) : (
        <TimeSlotManagement />
      )}
    </div>
  )
}
